import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Search, UserPlus, Edit2, Save,
  Briefcase, User, Mail, Phone, X,
  TrendingUp, ChevronDown, ChevronUp, Plus, Trash2,
} from 'lucide-react';

import { useEmployees, useEmployeesStore } from '../features/employees';
import { Button, Spinner, Badge } from '../ui/atoms';
import PhoneInput from '../components/PhoneInput';
import CustomDeleteDialog from '../components/CustomDeleteDialog';

Modal.setAppElement('#root');

// ─────────────────────────────────────────────────────────────
// Prim Ayarları Bölümü — kendi içinde izole alt bileşen
// ─────────────────────────────────────────────────────────────
function CommissionSection({ commissionConfig, services, store }) {
  const [serviceSelectId, setServiceSelectId] = useState('');
  const [serviceRate, setServiceRate]         = useState('');
  const [showPerService, setShowPerService]   = useState(false);

  const {
    enableCommission, disableCommission,
    updateCommissionField, setPerServiceRate, removePerServiceRate,
  } = store;

  const hasConfig = Boolean(commissionConfig);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Başlık + Aktif Toggle */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
        <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <TrendingUp className="h-4 w-4 text-indigo-500" />
          Prim Ayarları
        </span>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <span className="text-xs text-gray-500">{hasConfig ? 'Aktif' : 'Kapalı'}</span>
          <div
            onClick={() => (hasConfig ? disableCommission() : enableCommission())}
            className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${
              hasConfig ? 'bg-indigo-500' : 'bg-gray-300'
            }`}
          >
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              hasConfig ? 'translate-x-5' : 'translate-x-0.5'
            }`} />
          </div>
        </label>
      </div>

      {/* Ayarlar — yalnızca aktifse göster */}
      {hasConfig && (
        <div className="p-4 space-y-4">
          {/* Prim Türü */}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-2">Prim Türü</p>
            <div className="flex gap-2">
              {['percentage', 'fixed'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => updateCommissionField('type', type)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                    commissionConfig.type === type
                      ? 'bg-indigo-500 text-white border-indigo-500'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {type === 'percentage' ? 'Yüzde (%)' : 'Sabit (₺/seans)'}
                </button>
              ))}
            </div>
          </div>

          {/* Genel Oran */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Genel Oran {commissionConfig.type === 'percentage' ? '(%)' : '(₺/seans)'}
            </label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={commissionConfig.defaultRate ?? ''}
              onChange={(e) => updateCommissionField('defaultRate', parseFloat(e.target.value) || 0)}
              className="w-32 px-3 py-1.5 text-sm border border-gray-300 rounded-md
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Hizmet Bazlı Özel Oranlar */}
          <div>
            <button
              type="button"
              onClick={() => setShowPerService((v) => !v)}
              className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
            >
              {showPerService ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              Hizmet Bazlı Özel Oranlar (opsiyonel)
            </button>

            {showPerService && (
              <div className="mt-3 space-y-3">
                {/* Mevcut oranlar */}
                {Object.entries(commissionConfig.perServiceRates || {}).map(([svcId, rate]) => {
                  const svc = services.find((s) => s.id === svcId);
                  return (
                    <div key={svcId} className="flex items-center gap-2 text-sm">
                      <span className="flex-1 text-gray-700 truncate">
                        {svc?.name ?? svcId}
                      </span>
                      <span className="font-medium text-indigo-600 w-16 text-right">
                        {rate}{commissionConfig.type === 'percentage' ? '%' : '₺'}
                      </span>
                      <button
                        type="button"
                        onClick={() => removePerServiceRate(svcId)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}

                {/* Yeni oran ekleme */}
                <div className="flex items-center gap-2">
                  <select
                    value={serviceSelectId}
                    onChange={(e) => setServiceSelectId(e.target.value)}
                    className="flex-1 text-sm px-2 py-1.5 border border-gray-300 rounded-md
                      focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="">Hizmet seç…</option>
                    {services
                      .filter((s) => !(commissionConfig.perServiceRates?.[s.id] !== undefined))
                      .map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                  </select>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder="Oran"
                    value={serviceRate}
                    onChange={(e) => setServiceRate(e.target.value)}
                    className="w-20 text-sm px-2 py-1.5 border border-gray-300 rounded-md
                      focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!serviceSelectId || !serviceRate) return;
                      setPerServiceRate(serviceSelectId, parseFloat(serviceRate) || 0);
                      setServiceSelectId('');
                      setServiceRate('');
                    }}
                    className="p-1.5 text-white bg-indigo-500 hover:bg-indigo-600 rounded-md transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Ana Sayfa
// ─────────────────────────────────────────────────────────────
export default function Employee() {
  const { t } = useTranslation('services');

  // Server state + actions
  const { filteredEmployees, services, loading, saving, save, remove } = useEmployees();

  // UI state (seçici ile — yalnızca ilgili dilime abone ol)
  const searchTerm      = useEmployeesStore((s) => s.searchTerm);
  const setSearchTerm   = useEmployeesStore((s) => s.setSearchTerm);
  const isModalOpen     = useEmployeesStore((s) => s.isModalOpen);
  const isEditing       = useEmployeesStore((s) => s.isEditing);
  const formData        = useEmployeesStore((s) => s.formData);
  const openModal       = useEmployeesStore((s) => s.openModal);
  const closeModal      = useEmployeesStore((s) => s.closeModal);
  const updateFormField = useEmployeesStore((s) => s.updateFormField);

  // CommissionSection'a geçilecek store action'ları — her biri ayrı selector
  // (nesne literal selector her render'da yeni referans üretir → sonsuz döngü)
  const enableCommission      = useEmployeesStore((s) => s.enableCommission);
  const disableCommission     = useEmployeesStore((s) => s.disableCommission);
  const updateCommissionField = useEmployeesStore((s) => s.updateCommissionField);
  const setPerServiceRate     = useEmployeesStore((s) => s.setPerServiceRate);
  const removePerServiceRate  = useEmployeesStore((s) => s.removePerServiceRate);
  const commissionStoreActions = {
    enableCommission, disableCommission,
    updateCommissionField, setPerServiceRate, removePerServiceRate,
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <ToastContainer position="top-right" />

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder={t('employee.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <Button variant="primary" onClick={() => openModal()}>
          <UserPlus className="h-5 w-5" />
          {t('employee.newBtn')}
        </Button>
      </div>

      {/* ── Tablo ──────────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  'employee.colFirstName',
                  'employee.colLastName',
                  'employee.colEmail',
                  'employee.colPhone',
                ].map((key) => (
                  <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t(key)}
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prim
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('employee.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEmployees.map((emp) => (
                <tr key={emp.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">{emp.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{emp.surname}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{emp.emailAddress}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {emp.phone?.countryCode} {emp.phone?.number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {emp.commissionConfig ? (
                      <Badge color="indigo" size="xs">
                        {emp.commissionConfig.type === 'percentage'
                          ? `%${emp.commissionConfig.defaultRate}`
                          : `₺${emp.commissionConfig.defaultRate}/seans`}
                      </Badge>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => openModal(emp)}
                      className="inline-flex items-center px-3 py-1 text-sm text-blue-600
                        hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors mr-2"
                    >
                      <Edit2 className="h-4 w-4 mr-1" />
                      {t('employee.editBtn')}
                    </button>
                    <CustomDeleteDialog
                      onConfirm={() => remove(emp.id)}
                      entityType="calisan"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modal ──────────────────────────────────────────── */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        style={{
          overlay: {
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          },
          content: {
            position: 'relative',
            top: 'auto', left: 'auto', right: 'auto', bottom: 'auto',
            maxWidth: '520px',
            width: '90%',
            margin: '0 auto',
            padding: 0,
            border: 'none',
            background: 'transparent',
            overflow: 'visible',
          },
        }}
      >
        <div className="bg-white rounded-lg shadow-xl overflow-visible">
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              {isEditing ? t('employee.modalTitleEdit') : t('employee.modalTitleNew')}
            </h2>
            <button onClick={closeModal} className="text-white hover:text-gray-200 transition-colors">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Modal Body — kaydırılabilir */}
          <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Ad */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <User className="h-4 w-4 text-blue-500" />
                {t('employee.firstNameLabel')}
              </label>
              <input
                type="text"
                placeholder={t('employee.firstNamePlaceholder')}
                value={formData.name}
                onChange={(e) => updateFormField('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Soyad */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <User className="h-4 w-4 text-blue-500" />
                {t('employee.lastNameLabel')}
              </label>
              <input
                type="text"
                placeholder={t('employee.lastNamePlaceholder')}
                value={formData.surname}
                onChange={(e) => updateFormField('surname', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* E-posta */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Mail className="h-4 w-4 text-blue-500" />
                {t('employee.emailLabel')}
              </label>
              <input
                type="email"
                placeholder={t('employee.emailPlaceholder')}
                value={formData.emailAddress}
                onChange={(e) => updateFormField('emailAddress', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Telefon */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Phone className="h-4 w-4 text-blue-500" />
                {t('employee.phoneLabel')}
              </label>
              <PhoneInput
                value={formData.phone}
                onChange={(phoneData) => updateFormField('phone', phoneData)}
              />
            </div>

            {/* Prim Ayarları */}
            <CommissionSection
              commissionConfig={formData.commissionConfig}
              services={services}
              store={commissionStoreActions}
            />
          </div>

          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
            <Button variant="ghost" onClick={closeModal} disabled={saving}>
              {t('employee.cancelBtn')}
            </Button>
            <Button variant="primary" loading={saving} onClick={save}>
              <Save className="h-4 w-4" />
              {isEditing ? t('employee.updateBtn') : t('employee.saveBtn')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
